import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Workflow, Plus, Play, Archive, ChevronRight, Pencil, GitBranch,
  PauseCircle, Copy, Eye, Search, ChevronUp, ChevronDown, ChevronsUpDown,
  Activity, X, RotateCcw, Bookmark,
} from 'lucide-react';
import { TemplateLibrary } from '../components/automations/TemplateLibrary.tsx';
import type { TemplateItem } from '../components/automations/TemplateCard.tsx';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input, Select } from '../components/ui/Input.tsx';
import { Textarea } from '../components/ui/Input.tsx';
import { AutomationStatusBadge } from '../components/ui/Badge.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { Pagination } from '../components/ui/Pagination.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Trigger {
  id: string;
  triggerType: string;
  eventType?: string | null;
  scheduleExpression?: string | null;
}

interface Automation {
  id: string;
  name: string;
  description: string | null;
  type: 'journey' | 'campaign';
  status: string;
  concurrencyMode?: string | null;
  createdAt: string;
  triggers?: Trigger[];
}

type ConcurrencyMode = 'skip' | 'allow_parallel' | 'restart';
type SortCol = 'name' | 'createdAt';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 25;

function fmt(d: string) {
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// ─── Sort icon ────────────────────────────────────────────────────────────────

function SortIcon({ col, sort }: { col: SortCol; sort: { col: SortCol; dir: SortDir } }) {
  if (sort.col !== col) return <ChevronsUpDown size={11} className="text-zinc-300 ml-0.5 inline" />;
  return sort.dir === 'asc'
    ? <ChevronUp size={11} className="text-zinc-500 ml-0.5 inline" />
    : <ChevronDown size={11} className="text-zinc-500 ml-0.5 inline" />;
}

// ─── Status tabs ──────────────────────────────────────────────────────────────

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'paused', label: 'Paused' },
  { key: 'draft', label: 'Draft' },
  { key: 'archived', label: 'Archived' },
] as const;

// ─── Indeterminate checkbox ───────────────────────────────────────────────────

function IndeterminateCheckbox({
  checked,
  indeterminate,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="rounded border-zinc-300 text-violet-600 focus:ring-violet-200 cursor-pointer"
    />
  );
}

// ─── Edit Journey Modal (draft metadata only) ─────────────────────────────────

function EditAutomationModal({
  automation,
  onClose,
  onSaved,
}: {
  automation: Automation | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(automation?.name ?? '');
  const [description, setDescription] = useState(automation?.description ?? '');
  const [concurrencyMode, setConcurrencyMode] = useState<ConcurrencyMode>(
    (automation?.concurrencyMode as ConcurrencyMode) ?? 'skip',
  );

  const updateMutation = useMutation({
    mutationFn: () =>
      api.patch(`/api/v1/automations/${automation!.id}`, {
        name,
        description: description || undefined,
        concurrencyMode,
      }),
    onSuccess: () => {
      onSaved();
      toast.success('Automation updated');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to update automation')),
  });

  if (!automation) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit: ${automation.name}`}
      description="Only draft automations can be edited."
    >
      <div className="space-y-4">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Textarea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
        />
        <Select
          label="Concurrency mode"
          value={concurrencyMode}
          onChange={(e) => setConcurrencyMode(e.target.value as ConcurrencyMode)}
        >
          <option value="skip">Skip (don't re-enter)</option>
          <option value="allow_parallel">Allow parallel runs</option>
          <option value="restart">Restart from beginning</option>
        </Select>
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={updateMutation.isPending}
            disabled={!name.trim()}
            onClick={() => updateMutation.mutate()}
          >
            Save changes
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}

// ─── Save as Template Modal ───────────────────────────────────────────────────

function SaveAsTemplateModal({
  automation,
  onClose,
  onSaved,
}: {
  automation: Automation | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(automation ? `${automation.name} (template)` : '');
  const [tags, setTags] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/automations/${automation!.id}/save-as-template`, {
        name: name || undefined,
        tags: tags ? tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      }),
    onSuccess: () => {
      toast.success('Saved as template');
      onSaved();
    },
    onError: (err) => toast.error(extractError(err, 'Failed to save template')),
  });

  if (!automation) return null;

  return (
    <Modal open onClose={onClose} title="Save as template">
      <div className="space-y-3">
        <Input
          label="Template name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={`${automation.name} (template)`}
        />
        <Input
          label="Tags (comma-separated)"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="loyalty, onboarding"
        />
      </div>
      <ModalFooter>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" loading={mutation.isPending} onClick={() => mutation.mutate()}>
          Save template
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Create Automation Modal ──────────────────────────────────────────────────

function CreateAutomationModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const toast = useToast();
  const [mode, setMode] = useState<'scratch' | 'template'>('scratch');
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateItem | null>(null);
  const [name, setName] = useState('');

  const createMutation = useMutation({
    mutationFn: () =>
      api
        .post('/api/v1/automations', {
          name: name || 'Untitled Automation',
          type: 'journey',
          concurrencyMode: 'skip',
        })
        .then((r) => r.data as { id: string }),
    onSuccess: (def) => onCreated(def.id),
    onError: (err) => toast.error(extractError(err, 'Failed to create automation')),
  });

  const instantiateMutation = useMutation({
    mutationFn: () =>
      api
        .post(`/api/v1/automation-templates/${selectedTemplate!.id}/instantiate`, {
          name: name || selectedTemplate!.name,
        })
        .then((r) => r.data as { id: string }),
    onSuccess: (def) => onCreated(def.id),
    onError: (err) => toast.error(extractError(err, 'Failed to use template')),
  });

  function handleCreate() {
    if (mode === 'template' && selectedTemplate) {
      instantiateMutation.mutate();
    } else if (mode === 'scratch') {
      createMutation.mutate();
    }
  }

  const isLoading = createMutation.isPending || instantiateMutation.isPending;

  return (
    <Modal open onClose={onClose} title="New automation">
      <div className="space-y-4">
        {/* Mode selection */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => { setMode('scratch'); setSelectedTemplate(null); }}
            className={`border-2 rounded-xl p-4 text-center transition-all ${
              mode === 'scratch' ? 'border-violet-500 bg-violet-50' : 'border-zinc-200 hover:border-zinc-300'
            }`}
          >
            <div className="text-2xl mb-1">✏️</div>
            <div className="text-xs font-semibold text-zinc-800">From scratch</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">Empty builder</div>
          </button>
          <button
            onClick={() => setMode('template')}
            className={`border-2 rounded-xl p-4 text-center transition-all ${
              mode === 'template' ? 'border-violet-500 bg-violet-50' : 'border-zinc-200 hover:border-zinc-300'
            }`}
          >
            <div className="text-2xl mb-1">📚</div>
            <div className="text-xs font-semibold text-zinc-800">From template</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">Pre-built automations</div>
          </button>
        </div>

        {/* Template library (compact) */}
        {mode === 'template' && (
          <div className="border border-zinc-200 rounded-xl p-3 max-h-72 overflow-y-auto">
            {selectedTemplate ? (
              <div className="flex items-center justify-between bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
                <div>
                  <div className="text-xs font-semibold text-violet-800">{selectedTemplate.name}</div>
                  <div className="text-[11px] text-violet-600">
                    {selectedTemplate.source === 'platform' ? 'Platform template' : 'My template'}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedTemplate(null)}
                  className="p-1 text-violet-400 hover:text-violet-700"
                >
                  <X size={12} />
                </button>
              </div>
            ) : (
              <TemplateLibrary compact onSelect={(t) => setSelectedTemplate(t)} />
            )}
          </div>
        )}

        {/* Name field */}
        <Input
          label={mode === 'template' && selectedTemplate ? 'Automation name' : 'Name (optional)'}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={
            mode === 'template' && selectedTemplate
              ? selectedTemplate.name
              : 'Untitled Automation'
          }
        />
      </div>
      <ModalFooter>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          loading={isLoading}
          disabled={mode === 'template' && !selectedTemplate}
          onClick={handleCreate}
        >
          Create
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AutomationsPage() {
  const [editAutomation, setEditAutomation] = useState<Automation | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<Automation | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<Automation | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saveAsTemplateTarget, setSaveAsTemplateTarget] = useState<Automation | null>(null);

  // ── Filter / sort / page state ───────────────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ col: SortCol; dir: SortDir }>({ col: 'createdAt', dir: 'desc' });
  const [page, setPage] = useState(1);

  // ── Bulk select state ────────────────────────────────────────────────────────
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  // ── Query ────────────────────────────────────────────────────────────────────

  const { data = [], isLoading } = useQuery<Automation[]>({
    queryKey: ['automations', 'journeys'],
    queryFn: () =>
      api
        .get('/api/v1/automations')
        .then((r) => (r.data as Automation[]).filter((a) => a.type === 'journey')),
  });

  // ── Derived counts for tabs ──────────────────────────────────────────────────

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: data.length };
    for (const a of data) {
      c[a.status] = (c[a.status] ?? 0) + 1;
    }
    return c;
  }, [data]);

  // ── Filtered + sorted list (all pages) ───────────────────────────────────────

  const displayed = useMemo(() => {
    let list = data;
    if (statusFilter !== 'all' && statusFilter !== 'templates') list = list.filter((a) => a.status === statusFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((a) => a.name.toLowerCase().includes(q));
    }
    list = [...list].sort((a, b) => {
      const mul = sort.dir === 'asc' ? 1 : -1;
      if (sort.col === 'name') return mul * a.name.localeCompare(b.name);
      return mul * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    });
    return list;
  }, [data, statusFilter, search, sort]);

  // ── Pagination ────────────────────────────────────────────────────────────────

  const pageCount = Math.max(1, Math.ceil(displayed.length / PAGE_SIZE));
  const pageItems = displayed.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ── Helpers that reset page ───────────────────────────────────────────────────

  function setStatusFilterAndReset(v: string) {
    setStatusFilter(v);
    setPage(1);
    setSelected(new Set());
  }

  function setSearchAndReset(v: string) {
    setSearch(v);
    setPage(1);
    setSelected(new Set());
  }

  function toggleSort(col: SortCol) {
    setSort((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: 'asc' },
    );
    setPage(1);
    setSelected(new Set());
  }

  function onPageChange(p: number) {
    setPage(p);
    setSelected(new Set());
  }

  // ── Bulk select helpers ───────────────────────────────────────────────────────

  const allSelected = pageItems.length > 0 && pageItems.every((a) => selected.has(a.id));
  const someSelected = pageItems.some((a) => selected.has(a.id)) && !allSelected;

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (allSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        pageItems.forEach((a) => next.delete(a.id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        pageItems.forEach((a) => next.add(a.id));
        return next;
      });
    }
  }

  // ── Bulk mutations ────────────────────────────────────────────────────────────

  async function bulkDeactivate() {
    const targets = [...selected].filter(
      (id) => data.find((a) => a.id === id)?.status === 'active',
    );
    if (!targets.length) return;
    setBulkLoading(true);
    try {
      const results = await Promise.allSettled(
        targets.map((id) => api.post(`/api/v1/automations/${id}/deactivate`)),
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const fail = results.filter((r) => r.status === 'rejected').length;
      qc.invalidateQueries({ queryKey: ['automations'] });
      setSelected(new Set());
      if (fail > 0) toast.error(`${ok} paused, ${fail} failed`);
      else toast.info(`${ok} automation${ok !== 1 ? 's' : ''} paused`);
    } finally {
      setBulkLoading(false);
    }
  }

  async function bulkReactivate() {
    const targets = [...selected].filter(
      (id) => data.find((a) => a.id === id)?.status === 'paused',
    );
    if (!targets.length) return;
    setBulkLoading(true);
    try {
      const results = await Promise.allSettled(
        targets.map((id) => api.post(`/api/v1/automations/${id}/reactivate`)),
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const fail = results.filter((r) => r.status === 'rejected').length;
      qc.invalidateQueries({ queryKey: ['automations'] });
      setSelected(new Set());
      if (fail > 0) toast.error(`${ok} reactivated, ${fail} failed`);
      else toast.success(`${ok} automation${ok !== 1 ? 's' : ''} reactivated`);
    } finally {
      setBulkLoading(false);
    }
  }

  async function bulkArchive() {
    const targets = [...selected].filter(
      (id) => data.find((a) => a.id === id)?.status !== 'archived',
    );
    if (!targets.length) return;
    setBulkLoading(true);
    try {
      const results = await Promise.allSettled(
        targets.map((id) => api.delete(`/api/v1/automations/${id}`)),
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const fail = results.filter((r) => r.status === 'rejected').length;
      qc.invalidateQueries({ queryKey: ['automations'] });
      setSelected(new Set());
      if (fail > 0) toast.error(`${ok} archived, ${fail} failed`);
      else toast.info(`${ok} automation${ok !== 1 ? 's' : ''} archived`);
    } finally {
      setBulkLoading(false);
    }
  }

  // ── Per-item mutations ────────────────────────────────────────────────────────

  const archiveMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/automations/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automations'] });
      setArchiveTarget(null);
      toast.info('Automation archived');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const publishMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/automations/${id}/publish`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automations'] });
      toast.success('Automation published');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/automations/${id}/deactivate`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automations'] });
      setDeactivateTarget(null);
      toast.info('Automation deactivated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/automations/${id}/reactivate`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automations'] });
      toast.success('Automation reactivated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/automations/${id}/duplicate`),
    onSuccess: (res) => {
      const newDef = res.data as { id: string };
      toast.success('Automation duplicated');
      navigate(`/automations/${newDef.id}/build`);
    },
    onError: (err) => toast.error(extractError(err)),
  });

  // ── Bulk bar visibility helpers ───────────────────────────────────────────────

  const hasActiveSelected = [...selected].some(
    (id) => data.find((a) => a.id === id)?.status === 'active',
  );
  const hasPausedSelected = [...selected].some(
    (id) => data.find((a) => a.id === id)?.status === 'paused',
  );
  const hasArchivableSelected = [...selected].some(
    (id) => data.find((a) => a.id === id)?.status !== 'archived',
  );

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div>
      <PageHeader
        title="Automations"
        description="Visual per-customer automation flows — built in the visual builder"
        action={
          <Button size="sm" onClick={() => setShowCreateModal(true)}>
            <Plus size={13} className="mr-1.5" /> New automation
          </Button>
        }
      />

      {/* ── Toolbar: search + status tabs ─────────────────────────────────── */}
      {!isLoading && data.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-3">
          {/* Search */}
          {statusFilter !== 'templates' && (
            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search automations…"
                value={search}
                onChange={(e) => setSearchAndReset(e.target.value)}
                className="w-full pl-7 pr-3 py-1.5 text-xs border border-zinc-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 placeholder-zinc-400"
              />
            </div>
          )}

          {/* Status tabs */}
          <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1">
            {STATUS_TABS.map((tab) => {
              const count = counts[tab.key] ?? 0;
              const active = statusFilter === tab.key;
              if (tab.key !== 'all' && !count) return null;
              return (
                <button
                  key={tab.key}
                  onClick={() => setStatusFilterAndReset(tab.key)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
                    active
                      ? 'bg-white text-zinc-900 shadow-sm'
                      : 'text-zinc-500 hover:text-zinc-700'
                  }`}
                >
                  {tab.label}
                  <span
                    className={`text-[10px] px-1 rounded-full font-mono ${
                      active ? 'bg-zinc-100 text-zinc-600' : 'text-zinc-400'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Template library — visually separated */}
          <button
            onClick={() => setStatusFilterAndReset(statusFilter === 'templates' ? 'all' : 'templates')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg font-medium border transition-colors ${
              statusFilter === 'templates'
                ? 'bg-violet-600 text-white border-violet-600 shadow-sm'
                : 'bg-white text-violet-600 border-violet-200 hover:bg-violet-50'
            }`}
          >
            <Bookmark size={12} />
            Automation Library
          </button>
        </div>
      )}

      {statusFilter === 'templates' ? (
        <div className="bg-white border border-zinc-200 rounded-xl p-4">
          <TemplateLibrary />
        </div>
      ) : (
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100">
              {/* Checkbox header */}
              <th className="px-4 py-3 w-10">
                {!isLoading && pageItems.length > 0 && (
                  <IndeterminateCheckbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleSelectAll}
                  />
                )}
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider cursor-pointer select-none hover:text-zinc-600"
                onClick={() => toggleSort('name')}
              >
                Name <SortIcon col="name" sort={sort} />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Triggers
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Concurrency
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider cursor-pointer select-none hover:text-zinc-600"
                onClick={() => toggleSort('createdAt')}
              >
                Created <SortIcon col="createdAt" sort={sort} />
              </th>
              <th className="px-4 py-3 w-40" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-50">
            {isLoading ? (
              <SkeletonRows count={5} cols={7} />
            ) : displayed.length === 0 && data.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <EmptyState
                    icon={<Workflow size={26} />}
                    title="No automations yet"
                    description="Create an automation to build visual per-customer flows."
                    action={{ label: '+ New automation', onClick: () => setShowCreateModal(true) }}
                  />
                </td>
              </tr>
            ) : displayed.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="py-12 text-center">
                    <Search size={22} className="text-zinc-300 mx-auto mb-2" />
                    <p className="text-sm text-zinc-400">No automations match your filters</p>
                    <button
                      className="text-xs text-violet-500 hover:underline mt-1"
                      onClick={() => { setSearchAndReset(''); setStatusFilterAndReset('all'); }}
                    >
                      Clear filters
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              pageItems.map((a) => (
                <tr
                  key={a.id}
                  className={`transition-colors group cursor-pointer ${
                    selected.has(a.id) ? 'bg-violet-50/60' : 'hover:bg-zinc-50/60'
                  }`}
                  onClick={() => navigate(`/automations/${a.id}`)}
                >
                  {/* Checkbox */}
                  <td
                    className="px-4 py-3"
                    onClick={(e) => { e.stopPropagation(); toggleSelect(a.id); }}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(a.id)}
                      onChange={() => toggleSelect(a.id)}
                      className="rounded border-zinc-300 text-violet-600 focus:ring-violet-200 cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-zinc-900">{a.name}</p>
                    {a.description && (
                      <p className="text-xs text-zinc-400 mt-0.5 truncate max-w-[220px]">
                        {a.description}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <AutomationStatusBadge status={a.status} />
                  </td>
                  <td className="px-4 py-3">
                    {a.triggers && a.triggers.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {a.triggers.slice(0, 2).map((t) => (
                          <code
                            key={t.id}
                            className="text-[10px] bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded font-mono"
                          >
                            {t.eventType ?? t.scheduleExpression ?? t.triggerType}
                          </code>
                        ))}
                        {a.triggers.length > 2 && (
                          <span className="text-[10px] text-zinc-400">
                            +{a.triggers.length - 2}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-zinc-300">manual</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {a.concurrencyMode ? (
                      <code className="text-[10px] bg-zinc-50 text-zinc-500 px-1.5 py-0.5 rounded font-mono">
                        {a.concurrencyMode}
                      </code>
                    ) : (
                      <span className="text-xs text-zinc-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500">{fmt(a.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {/* Executions/logs — sempre visibile */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/automations/${a.id}`);
                        }}
                        className="p-1.5 text-zinc-300 hover:text-violet-600 hover:bg-violet-50 rounded-md transition-colors"
                        title="View executions & logs"
                      >
                        <Activity size={12} />
                      </button>

                      {/* Hover-only actions */}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {a.status === 'draft' && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditAutomation(a);
                              }}
                              className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                              title="Edit metadata"
                            >
                              <Pencil size={11} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/automations/${a.id}/build`);
                              }}
                              className="p-1.5 text-zinc-400 hover:text-violet-600 hover:bg-violet-50 rounded-md"
                              title="Open in Builder"
                            >
                              <GitBranch size={11} />
                            </button>
                            <Button
                              size="sm"
                              variant="ghost"
                              loading={publishMutation.isPending}
                              onClick={(e) => {
                                e.stopPropagation();
                                publishMutation.mutate(a.id);
                              }}
                            >
                              <Play size={11} className="mr-1" /> Publish
                            </Button>
                          </>
                        )}
                        {(a.status === 'active' || a.status === 'paused') && a.type === 'journey' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/automations/${a.id}/build`);
                            }}
                            className="p-1.5 text-zinc-400 hover:text-violet-600 hover:bg-violet-50 rounded-md"
                            title="View in Builder"
                          >
                            <Eye size={11} />
                          </button>
                        )}
                        {a.status === 'active' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeactivateTarget(a);
                            }}
                            className="p-1.5 text-zinc-400 hover:text-amber-600 hover:bg-amber-50 rounded-md"
                            title="Deactivate"
                          >
                            <PauseCircle size={11} />
                          </button>
                        )}
                        {a.status === 'paused' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              reactivateMutation.mutate(a.id);
                            }}
                            className="p-1.5 text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-md"
                            title="Reactivate"
                          >
                            <Play size={11} />
                          </button>
                        )}
                        {a.status !== 'archived' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              duplicateMutation.mutate(a.id);
                            }}
                            className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                            title="Duplicate"
                          >
                            <Copy size={11} />
                          </button>
                        )}
                        {a.status !== 'archived' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSaveAsTemplateTarget(a);
                            }}
                            className="p-1.5 text-zinc-400 hover:text-violet-600 hover:bg-violet-50 rounded-md"
                            title="Save as template"
                          >
                            <Bookmark size={11} />
                          </button>
                        )}
                        {a.status !== 'archived' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setArchiveTarget(a);
                            }}
                            className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                            title="Archive"
                          >
                            <Archive size={11} />
                          </button>
                        )}
                        <ChevronRight size={13} className="text-zinc-300" />
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* ── Pagination ─────────────────────────────────────────────────── */}
        {pageCount > 1 && (
          <div className="border-t border-zinc-100">
            <Pagination page={page} totalPages={pageCount} onPageChange={onPageChange} />
          </div>
        )}
      </div>
      )}

      {/* ── Bulk action bar ───────────────────────────────────────────────── */}
      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 bg-zinc-900 text-white rounded-2xl shadow-2xl px-4 py-2.5 text-xs font-medium">
          <span className="text-zinc-400 pr-1">{selected.size} selected</span>
          <div className="w-px h-4 bg-zinc-700 mx-1" />

          {hasPausedSelected && (
            <button
              disabled={bulkLoading}
              onClick={bulkReactivate}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 transition-colors"
            >
              <RotateCcw size={11} /> Reactivate
            </button>
          )}
          {hasActiveSelected && (
            <button
              disabled={bulkLoading}
              onClick={bulkDeactivate}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-50 transition-colors"
            >
              <PauseCircle size={11} /> Pause
            </button>
          )}
          {hasArchivableSelected && (
            <button
              disabled={bulkLoading}
              onClick={bulkArchive}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 transition-colors"
            >
              <Archive size={11} /> Archive
            </button>
          )}

          <div className="w-px h-4 bg-zinc-700 mx-1" />
          <button
            onClick={() => setSelected(new Set())}
            className="p-1 text-zinc-400 hover:text-white rounded-lg transition-colors"
            title="Clear selection"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <EditAutomationModal
        automation={editAutomation}
        onClose={() => setEditAutomation(null)}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['automations'] });
          setEditAutomation(null);
        }}
      />

      <ConfirmDialog
        open={!!deactivateTarget}
        title="Deactivate automation?"
        message={`"${deactivateTarget?.name}" will be paused. No new triggers will fire, but running instances will complete. You can reactivate it later.`}
        confirmLabel="Deactivate"
        variant="warning"
        loading={deactivateMutation.isPending}
        onConfirm={() => deactivateTarget && deactivateMutation.mutate(deactivateTarget.id)}
        onCancel={() => setDeactivateTarget(null)}
      />

      <ConfirmDialog
        open={!!archiveTarget}
        title="Archive automation?"
        message={`"${archiveTarget?.name}" will be archived. Running instances will complete but no new triggers will fire.`}
        confirmLabel="Archive"
        variant="warning"
        loading={archiveMutation.isPending}
        onConfirm={() => archiveTarget && archiveMutation.mutate(archiveTarget.id)}
        onCancel={() => setArchiveTarget(null)}
      />

      {showCreateModal && (
        <CreateAutomationModal
          onClose={() => setShowCreateModal(false)}
          onCreated={(id) => {
            setShowCreateModal(false);
            navigate(`/automations/${id}/build`);
          }}
        />
      )}

      {saveAsTemplateTarget && (
        <SaveAsTemplateModal
          automation={saveAsTemplateTarget}
          onClose={() => setSaveAsTemplateTarget(null)}
          onSaved={() => setSaveAsTemplateTarget(null)}
        />
      )}
    </div>
  );
}
